import { useEffect, useState, useRef } from "react";
import { EyeOff, Eye } from "lucide-react";
import { motion, useAnimation } from "motion/react";
import { useInView } from "react-intersection-observer";
import { Wrapper, FieldWrapper, BtnAction, PasswordWrapper, PasswordVisibility, ErrorBox, LinkStyling } from "../../styles/formStyles.js";
import QRStep from "./components/QRstep.jsx";
import OTPStep from "./components/OTPstep.jsx";
import ForgotPasswordStep from "./components/ForgotPasswordStep.jsx";

const shakeVariants = {
	shake: {
		x: [0, -5, 5, -5, 5, 0],
		transition: { duration: 0.6 },
	},
};

const currentUrl = `${window.location.protocol}//${window.location.host}`;
const root = document.getElementById("ahg-login");
const dataset = root?.dataset;

const appendSsoCode = (url, code) => {
	if (!code) return url;
	return url + (url.includes("?") ? "&" : "?") + "code=" + encodeURIComponent(code);
};

const Employee = () => {
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [error, setError] = useState("");
	const [showError, setShowError] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [loading, setLoading] = useState(false);
	const [isRedirecting, setIsRedirecting] = useState(false);
	const [autoRedirecting, setAutoRedirecting] = useState(false);
	const [showForgot, setShowForgot] = useState(false);

	// MFA state
	const [step, setStep] = useState(localStorage.getItem("ahg_mfa_step") || "login"); // "login" | "qr" | "otp"
	const [mfaData, setMfaData] = useState(JSON.parse(localStorage.getItem("ahg_mfa_data")) || null); // { temp_token, qr_code, secret_string }

	const ref = useRef(null);
	const controls = useAnimation();
	const [inViewRef, inView] = useInView();

	useEffect(() => {
		if (ahgLogin.is_login) {
			setAutoRedirecting(true);

			const timer = setTimeout(() => {
				window.location.href = appendSsoCode(dataset.redirectUrl, ahgLogin.sso_code);
			}, 1200);

			return () => clearTimeout(timer);
		}
	}, []);

	useEffect(() => {
		localStorage.setItem("ahg_mfa_step", step);
	}, [step]);

	// Abandon the MFA challenge entirely. The email is kept; the password must be re-entered,
	// since the pending temp_token is discarded and the next login issues a fresh one.
	const resetToLogin = () => {
		localStorage.removeItem("ahg_mfa_data");
		setMfaData(null);
		setStep("login");
		setPassword("");
		setError("");
		setShowError(false);
	};

	const setRefs = (node) => {
		ref.current = node;
		inViewRef(node);
	};

	useEffect(() => {
		if (showError && error && inView) {
			controls.start("shake");
		}
	}, [showError, error, inView]);

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (loading) return;
		setShowError(false);
		setLoading(true);

		try {
			const response = await fetch(`${currentUrl}/wp-json/ahg/v1/login/`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					"X-WP-Nonce": ahgLogin.nonce,
				},
				body: JSON.stringify({
					username: email,
					password: password,
				}),
			});

			const data = await response.json();

			if (data.code) {
				setError(data.message);
				setShowError(true);
				setLoading(false);
				return;
			}

			setLoading(false);

			if (data.mfa_required) {
				const dataObj = {
					temp_token: data.temp_token,
					qr_code: data.qr_code,
					secret_string: data.secret_string,
					is_new_setup: data.is_new_setup,
				};

				setMfaData(dataObj);
				localStorage.setItem("ahg_mfa_data", JSON.stringify(dataObj));
				setStep(data.is_new_setup ? "qr" : "otp");
			} else {
				setIsRedirecting(true);
				window.location.href = appendSsoCode(dataset.redirectUrl, data.sso_code);
			}
		} catch (err) {
			console.error("Error during login:", err);
			setLoading(false);
		}
	};

	if (autoRedirecting) {
		return (
			<Wrapper>
				<div style={{ textAlign: "center", padding: "40px 20px" }}>
					<p style={{ fontWeight: 700, marginBottom: 8, color: "#FFFFFF", letterSpacing: 2, fontSize: "1.5rem" }}>
						You are already logged in.
					</p>
					<p style={{ color: "#3A7DC9", fontSize: "0.9rem" }}>Redirecting you to dashboard...</p>
				</div>
			</Wrapper>
		);
	}

	// ── QR Step ──────────────────────────────────────────────────────────────
	if (step === "qr" && mfaData) {
		return <QRStep qrCode={mfaData.qr_code} secretString={mfaData.secret_string} onNext={() => setStep("otp")} onBackToLogin={resetToLogin} />;
	}

	// ── OTP Step ─────────────────────────────────────────────────────────────
	// onBack goes one step back (the QR for a first-time setup, else the login form);
	// onBackToLogin always returns to the login form.
	if (step === "otp" && mfaData) {
		return (
			<OTPStep
				tempToken={mfaData.temp_token}
				onBack={mfaData.is_new_setup ? () => setStep("qr") : resetToLogin}
				onBackToLogin={resetToLogin}
				redirectUrl={dataset.redirectUrl}
			/>
		);
	}

	// ── Forgot Password ───────────────────────────────────────────────────────
	if (showForgot) {
		return <ForgotPasswordStep initialEmail={email} onBack={() => setShowForgot(false)} />;
	}

	// ── Login Form ────────────────────────────────────────────────────────────
	return (
		<Wrapper>
			<form onSubmit={handleSubmit}>
				{error && (
					<ErrorBox>
						{showError && (
							<motion.div ref={setRefs} variants={shakeVariants} initial={false} animate={controls}>
								{error}
							</motion.div>
						)}
					</ErrorBox>
				)}

				<FieldWrapper>
					<label htmlFor="user_login">Email Address</label>
					<input type="email" name="user_login" value={email} onChange={(e) => setEmail(e.target.value)} />
				</FieldWrapper>

				<FieldWrapper>
					<label htmlFor="user_pass">Password</label>
					<PasswordWrapper>
						<input
							type={showPassword ? "text" : "password"}
							name="user_pass"
							value={password}
							onChange={(e) => setPassword(e.target.value)}
						/>
						<PasswordVisibility type="button" onClick={() => setShowPassword(!showPassword)}>
							{showPassword ? <EyeOff /> : <Eye />}
						</PasswordVisibility>
					</PasswordWrapper>
				</FieldWrapper>

				<p>
					<BtnAction type="submit" disabled={loading || isRedirecting}>
						{isRedirecting ? "Redirecting..." : loading ? "Signing in..." : "Sign in"}
					</BtnAction>
				</p>

				<LinkStyling
					href="#"
					onClick={(e) => {
						e.preventDefault();
						setShowForgot(true);
					}}
				>
					Forgot password?
				</LinkStyling>
			</form>
		</Wrapper>
	);
};

export default Employee;
